<div data-testid="confirm-vendor-page" class="app-confirm-vendor">
  <!-- Sticky Header -->
  <div class="sticky-header">
    <div class="flex justify-between items-center mb-5">
      <div class="flex items-center gap-3">
        <h2 class="text-2xl font-semibold text-gray-800">
          {{ "purchase_order.confirm_vendor.title" | translate }}
        </h2>
        @if (loading()) {
          <i class="pi pi-spin pi-spinner text-xl text-gray-600"></i>
        }
      </div>
      <div class="flex gap-3">
        <app-button
          [testId]="'confirm-vendor-cancel-btn'"
          [label]="'common.actions.cancel' | translate"
          (emitOnClick)="onCancel()"
          [outlined]="true"
          [type]="'secondary'"
        ></app-button>
        <app-button
          [testId]="'confirm-vendor-confirm-btn'"
          [label]="'purchase_order.confirm_vendor.confirm_button' | translate"
          (emitOnClick)="onSave()"
          [type]="'info'"
          [loading]="loading()"
        ></app-button>
      </div>
    </div>
  </div>

  <!-- Content -->
  <div class="confirm-vendor-content">
    @if (loading()) {
      <div class="flex justify-center items-center py-8">
        <i class="pi pi-spin pi-spinner text-2xl"></i>
      </div>
    } @else {
      <form data-testid="confirm-vendor-form" [formGroup]="confirmForm" class="space-y-6">
        <!-- PO Information (Read-only) -->
        <p-card styleClass="shadow-sm border-0">
          <ng-template pTemplate="header">
            <div class="flex items-center">
              <h2 class="text-lg font-medium text-gray-800">
                {{ "purchase_order.confirm_vendor.po_information" | translate }}
              </h2>
            </div>
          </ng-template>
          <ng-template pTemplate="content">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div class="space-y-2">
                <span class="text-sm font-medium text-gray-600">
                  {{ "purchase_order.confirm_vendor.po_number" | translate }}
                </span>
                <p class="text-sm text-gray-800">
                  {{ poDetails()?.series || "-" }}
                </p>
              </div>
              <div class="space-y-2">
                <span class="text-sm font-medium text-gray-600">
                  {{ "purchase_order.confirm_vendor.vendor" | translate }}
                </span>
                <p class="text-sm text-gray-800">
                  {{
                    poDetails()?.vendor?.vendorName ||
                      poDetails()?.vendorCode ||
                      "-"
                  }}
                </p>
              </div>
              <div class="space-y-2">
                <span class="text-sm font-medium text-gray-600">
                  {{ "purchase_order.confirm_vendor.po_date" | translate }}
                </span>
                <p class="text-sm text-gray-800">
                  {{ formatDate(poDetails()?.creationDate) || "-" }}
                </p>
              </div>
              <div class="space-y-2">
                <span class="text-sm font-medium text-gray-600">
                  {{ "purchase_order.add.fields.purchase_request" | translate }}
                </span>
                <p class="text-sm text-gray-800">
                  {{ poDetails()?.purchaseRequest?.series || "-" }}
                </p>
              </div>
              <div class="space-y-2">
                <span class="text-sm font-medium text-gray-600">
                  {{ "purchase_order.add.fields.description" | translate }}
                </span>
                <p class="text-sm text-gray-800">
                  {{ poDetails()?.description || "-" }}
                </p>
              </div>
              <div class="space-y-2">
                <span class="text-sm font-medium text-gray-600">
                  {{
                    "purchase_order.confirm_vendor.delivery_date" | translate
                  }}
                </span>
                <p class="text-sm text-gray-800">
                  {{ formatDate(poDetails()?.deliveryDate) || "-" }}
                </p>
              </div>
            </div>
          </ng-template>
        </p-card>

        <!-- Delivery Plan per Warehouse -->
        <p-card styleClass="shadow-sm border-0">
          <ng-template pTemplate="header">
            <div class="flex items-center">
              <h2 class="text-lg font-medium text-gray-800">
                {{
                  "purchase_order.confirm_vendor.delivery_plan_title"
                    | translate
                }}
              </h2>
            </div>
          </ng-template>
          <ng-template pTemplate="content">
            <div class="space-y-4" formArrayName="warehousePlans">
              @for (
                planGroup of warehousePlansFormArray.controls;
                track $index
              ) {
                <div
                  class="border-b border-gray-300 pb-4 last:border-b-0 last:pb-0"
                  [formGroup]="getWarehousePlanFormGroup($index)"
                >
                  <div class="space-y-4">
                    <div class="flex items-center gap-2">
                      <span
                        class="text-sm font-medium text-gray-600 whitespace-nowrap"
                      >
                        {{
                          "purchase_order.confirm_vendor.warehouse" | translate
                        }}:
                      </span>
                      <p class="text-sm font-semibold text-gray-800">
                        {{
                          getWarehousePlanFormGroup($index).get("warehouseName")
                            ?.value ||
                            getWarehousePlanFormGroup($index).get(
                              "warehouseCode"
                            )?.value
                        }}
                      </p>
                    </div>
                    <div
                      class="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 items-center"
                    >
                      <div
                        class="flex flex-col sm:flex-row sm:items-center gap-2 expected-date-field"
                      >
                        <span
                          class="text-sm font-medium text-gray-600 whitespace-nowrap"
                        >
                          {{
                            "purchase_order.confirm_vendor.expected_arrival_date"
                              | translate
                          }}:
                        </span>
                        <div class="flex-1 datepicker-wrapper">
                          <app-advanced-datepicker
                            [attr.data-testid]="'confirm-vendor-expected-arrival-date-' + $index"
                            formControlName="expectedArrivalDate"
                            [minDate]="minDate"
                            [placeholder]="
                              'purchase_order.confirm_vendor.select_date'
                                | translate
                            "
                            styleClass="z-20"
                          ></app-advanced-datepicker>
                        </div>
                      </div>
                      <div
                        class="flex flex-col sm:flex-row sm:items-center gap-2 delivery-code-field"
                      >
                        <span
                          class="text-sm font-medium text-gray-600 whitespace-nowrap"
                        >
                          {{
                            "purchase_order.confirm_vendor.delivery_code"
                              | translate
                          }}:
                        </span>
                        <input
                          [attr.data-testid]="'confirm-vendor-delivery-code-' + $index"
                          type="text"
                          formControlName="asnCode"
                          class="w-48 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-400"
                          [placeholder]="
                            'purchase_order.confirm_vendor.delivery_code'
                              | translate
                          "
                        />
                      </div>
                      <div class="flex items-center justify-end">
                        <app-button
                          [testId]="'confirm-vendor-view-order-details-' + $index"
                          [label]="
                            'purchase_order.confirm_vendor.view_order_details'
                              | translate
                          "
                          (emitOnClick)="onViewOrderDetails($index)"
                          [outlined]="false"
                          [type]="'info'"
                          styleClass="text-xs px-3 py-1"
                        ></app-button>
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>
          </ng-template>
        </p-card>
      </form>
    }
  </div>
</div>

<!-- Order Details Dialog -->
@if (orderDetailsData()) {
  <app-order-details-dialog
    data-testid="confirm-vendor-order-details-dialog"
    [(visible)]="showOrderDetailsDialog"
    [warehouseCode]="orderDetailsData()!.warehouseCode"
    [warehouseName]="orderDetailsData()!.warehouseName"
    [orderItems]="orderDetailsData()!.orderItems"
    [tableColumns]="orderDetailsData()!.tableColumns"
    (visibleChange)="onOrderDetailsDialogClose()"
  ></app-order-details-dialog>
}

<p-toast data-testid="confirm-vendor-toast"></p-toast>
